import { Alert, Button, Input, Select, Space, Typography, theme } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  FORMATS,
  PATTERN_PRESETS,
  VALIDATOR_LABEL,
  describeVisibleWhen,
  validatorsFor,
  type Rules,
  type Validator,
  type ValidatorKind,
  type VisibleWhen,
} from "./rules";
import type { Node } from "./schema";

const { Text } = Typography;

type Props = {
  node: Node;
  /** Every other field in this form, for the "show only when…" picker. */
  others: Array<{ key: string; title: string; options: string[] }>;
  onChange: (rules: Rules) => void;
};

const Row = ({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ marginBottom: 4 }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {label}
      </Text>
    </div>
    {children}
    {hint && (
      <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
        {hint}
      </Text>
    )}
  </div>
);

const OPS: Array<{ value: VisibleWhen["op"]; label: string; needsValue: boolean }> = [
  { value: "eq", label: "is", needsValue: true },
  { value: "ne", label: "is not", needsValue: true },
  { value: "in", label: "is one of", needsValue: true },
  { value: "not_in", label: "is none of", needsValue: true },
  { value: "gt", label: "is more than", needsValue: true },
  { value: "lt", label: "is less than", needsValue: true },
  { value: "filled", label: "is answered", needsValue: false },
  { value: "empty", label: "is blank", needsValue: false },
];

/**
 * Validation and "show this only when…" for one field.
 *
 * Both were on the "cannot be authored here" list, and both forced an
 * administrator back to the legacy antd-4 application to do something ordinary:
 * "quantity must be at least 1", "only ask why a single supplier is acceptable
 * when sole-sourcing is ticked".
 *
 * Two design choices worth keeping:
 *
 * * **The rule is a picker, never a text box.** A field name typed by hand is a
 *   rule that silently never fires, which is the failure mode the process
 *   builder was designed around too. Only fields that come *earlier* in the
 *   form are offered — a question cannot depend on an answer given below it.
 * * **One clause, not a tree.** The process rule language has `all`/`any`
 *   because a step genuinely branches on several answers. A field that needs
 *   three conditions to decide whether to appear is a field in the wrong
 *   section, and offering the tree here would invite building one.
 */
const RulesEditor = ({ node, others, onChange }: Props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const kinds = validatorsFor(node.component);
  const rules = node.rules;
  const rule = rules.visibleWhen;

  const labelOf = (key: string) =>
    others.find((o) => o.key === key)?.title || key;

  if (node.foreignRules) {
    return (
      <Alert
        type="warning"
        showIcon
        style={{ marginTop: 8 }}
        message={t("This field has rules from the classic designer")}
        description={t(
          "Its validation or visibility was written elsewhere. It is kept exactly as it is and still works — editing it here could only overwrite it with a guess."
        )}
      />
    );
  }

  const setValidator = (index: number, patch: Partial<Validator>) =>
    onChange({
      ...rules,
      validators: rules.validators.map((v, i) =>
        i === index ? { ...v, ...patch } : v
      ),
    });

  const addValidator = () =>
    onChange({
      ...rules,
      validators: [...rules.validators, { kind: kinds[0], value: "", message: "" }],
    });

  const setRule = (patch: Partial<VisibleWhen>) =>
    onChange({
      ...rules,
      visibleWhen: { ...(rule ?? { field: "", op: "eq" }), ...patch } as VisibleWhen,
    });

  const chosen = others.find((o) => o.key === rule?.field);
  const opDef = OPS.find((o) => o.value === rule?.op);
  const multi = rule?.op === "in" || rule?.op === "not_in";

  return (
    <div>
      {kinds.length > 0 && (
        <Row
          label={t("Rules for the answer")}
          hint={t(
            "Checked while the person is filling the form in, so they can fix it before they submit."
          )}
        >
          <Space direction="vertical" size={6} style={{ width: "100%" }}>
            {rules.validators.map((validator, i) => (
              <div
                key={i}
                style={{
                  padding: 8,
                  borderRadius: token.borderRadius,
                  border: `1px solid ${token.colorBorderSecondary}`,
                  background: token.colorFillQuaternary,
                }}
              >
                <Space.Compact style={{ width: "100%" }}>
                  <Select
                    size="small"
                    style={{ width: "45%" }}
                    value={validator.kind}
                    onChange={(v) =>
                      setValidator(i, { kind: v as ValidatorKind, value: "" })
                    }
                    options={kinds.map((k) => ({
                      value: k,
                      label: t(VALIDATOR_LABEL[k]),
                    }))}
                  />
                  {validator.kind === "format" ? (
                    <Select
                      size="small"
                      style={{ width: "45%" }}
                      value={validator.value || undefined}
                      placeholder={t("Pick one")}
                      onChange={(v) => setValidator(i, { value: v })}
                      options={FORMATS.map((f) => ({
                        value: f.value,
                        label: t(f.label),
                      }))}
                    />
                  ) : validator.kind === "pattern" ? (
                    <Select
                      size="small"
                      showSearch
                      style={{ width: "45%" }}
                      value={validator.value || undefined}
                      placeholder={t("Pick or type a pattern")}
                      // `tags` rather than a plain select: the presets cover
                      // what these forms ask for, and the box stays open for
                      // somebody who genuinely knows regular expressions.
                      mode="tags"
                      maxTagCount={1}
                      onChange={(v) =>
                        setValidator(i, {
                          value: Array.isArray(v) ? String(v[v.length - 1] ?? "") : String(v),
                        })
                      }
                      options={PATTERN_PRESETS.map((p) => ({
                        value: p.value,
                        label: t(p.label),
                      }))}
                    />
                  ) : (
                    <Input
                      size="small"
                      style={{ width: "45%" }}
                      type="number"
                      value={validator.value}
                      placeholder={t("Value")}
                      onChange={(e) => setValidator(i, { value: e.target.value })}
                    />
                  )}
                  <Button
                    size="small"
                    icon={<DeleteOutlined />}
                    aria-label={t("Remove")}
                    onClick={() =>
                      onChange({
                        ...rules,
                        validators: rules.validators.filter((_, x) => x !== i),
                      })
                    }
                  />
                </Space.Compact>
                <Input
                  size="small"
                  style={{ marginTop: 6 }}
                  value={validator.message ?? ""}
                  placeholder={t("What to tell them if it fails (optional)")}
                  onChange={(e) => setValidator(i, { message: e.target.value })}
                />
              </div>
            ))}
            <Button size="small" icon={<PlusOutlined />} onClick={addValidator} block>
              {t("Add a rule")}
            </Button>
          </Space>
        </Row>
      )}

      <Row
        label={t("Show this field only when…")}
        hint={
          rule
            ? t("Otherwise it is hidden and nobody is asked to answer it.")
            : t(
                "Leave it empty and the field is always shown. Only questions asked earlier in this form can be used — a field cannot depend on an answer given below it."
              )
        }
      >
        {others.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("There is no earlier question in this form to depend on.")}
          </Text>
        ) : (
          <Space direction="vertical" size={6} style={{ width: "100%" }}>
            <Select
              size="small"
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: "100%" }}
              placeholder={t("Always shown")}
              value={rule?.field || undefined}
              onChange={(v) =>
                onChange({
                  ...rules,
                  visibleWhen: v ? { field: v, op: "eq", value: "" } : null,
                })
              }
              options={others.map((o) => ({ value: o.key, label: o.title || o.key }))}
            />
            {rule && (
              <>
                <Space.Compact style={{ width: "100%" }}>
                  <Select
                    size="small"
                    style={{ width: opDef?.needsValue ? "45%" : "100%" }}
                    value={rule.op}
                    onChange={(v) =>
                      setRule({ op: v, value: multi ? [] : "" })
                    }
                    options={OPS.map((o) => ({ value: o.value, label: t(o.label) }))}
                  />
                  {opDef?.needsValue &&
                    ((chosen?.options.length ?? 0) > 0 ? (
                      <Select
                        size="small"
                        style={{ width: "55%" }}
                        mode={multi ? "multiple" : undefined}
                        placeholder={t("Pick the answer")}
                        value={rule.value as any}
                        onChange={(v) => setRule({ value: v })}
                        options={(chosen?.options ?? []).map((o) => ({
                          value: o,
                          label: o,
                        }))}
                      />
                    ) : (
                      <Input
                        size="small"
                        style={{ width: "55%" }}
                        placeholder={t("Answer")}
                        value={
                          Array.isArray(rule.value)
                            ? rule.value.join(", ")
                            : rule.value ?? ""
                        }
                        onChange={(e) =>
                          setRule({
                            value: multi
                              ? e.target.value.split(",").map((x) => x.trim())
                              : e.target.value,
                          })
                        }
                      />
                    ))}
                </Space.Compact>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {t("Shown when {{rule}}.", {
                    rule: describeVisibleWhen(rule, labelOf),
                  })}
                </Text>
              </>
            )}
          </Space>
        )}
      </Row>
    </div>
  );
};

export default RulesEditor;
